import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Upload, Trash2, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { updateShopSettlement } from '../services/shopSettlementService';
import { uploadFile } from '../services/uploadService';

const PAYMENT_STATUS_OPTIONS = [
  { value: 0, label: 'Pending' },
  { value: 1, label: 'Paid' },
];

const formatAmount = (value) => {
  const parsed = Number.parseFloat(value);
  if (Number.isNaN(parsed)) {
    return '₹0.00';
  }
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(parsed);
};

export default function ShopSettlementEditModal({ isOpen, onClose, settlement, onUpdated }) {
  const [amountPaid, setAmountPaid] = useState('0');
  const [note, setNote] = useState('');
  const [status, setStatus] = useState(0);
  const [signatureUrl, setSignatureUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen || !settlement) {
      setAmountPaid('0');
      setNote('');
      setStatus(0);
      setSignatureUrl(null);
      return;
    }

    const normalizedAmount = Number.parseFloat(settlement.amountPaid || 0) || 0;
    setAmountPaid(normalizedAmount.toFixed(2));
    setNote(settlement.note || '');
    const normalizedStatus = Number.parseInt(settlement.paymentStatus, 10);
    setStatus(Number.isNaN(normalizedStatus) ? 0 : normalizedStatus);
    setSignatureUrl(settlement.signatureUrl || null);
  }, [isOpen, settlement]);

  const payableAmount = useMemo(() => {
    if (!settlement) return 0;
    const total = Number.parseFloat(settlement.totalOrderAmount || 0) || 0;
    const commission = Number.parseFloat(settlement.commissionAmount || 0) || 0;
    return Number((total - commission).toFixed(2));
  }, [settlement]);

  const balanceAmount = useMemo(() => {
    const paid = Number.parseFloat(amountPaid);
    if (Number.isNaN(paid)) {
      return payableAmount;
    }
    const balance = Number((payableAmount - paid).toFixed(2));
    return Math.abs(balance) < 0.01 ? 0 : balance;
  }, [amountPaid, payableAmount]);

  if (!isOpen || !settlement) {
    return null;
  }

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await uploadFile(file);
      const url = response.data?.data?.fileUrl;
      if (!url) {
        throw new Error('Upload failed');
      }
      setSignatureUrl(url);
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const parsedAmount = Number.parseFloat(amountPaid || '0');
    if (Number.isNaN(parsedAmount) || parsedAmount < 0) {
      toast.error('Enter a valid amount paid');
      return;
    }

    if (parsedAmount - payableAmount > 0.01) {
      toast.error('Amount paid cannot exceed the payable amount');
      return;
    }

    if (status === 1 && Math.abs(balanceAmount) >= 0.01) {
      toast.error('Clear the outstanding balance before marking as paid');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        amountPaid: Number(parsedAmount.toFixed(2)),
        paymentStatus: status,
        note: note.trim() ? note.trim() : null,
        signatureUrl: signatureUrl || null,
      };

      const response = await updateShopSettlement(settlement.id, payload);
      const updated = response.data?.data;
      toast.success('Settlement updated successfully');
      if (updated) {
        onUpdated?.(updated);
      } else {
        onUpdated?.({ ...settlement, ...payload, balanceAmount });
      }
      onClose?.();
    } catch (error) {
      console.error(error);
      const message = error.response?.data?.message || 'Failed to update settlement';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="absolute inset-0" onClick={() => !submitting && onClose?.()} />
      <div className="relative z-10 w-full max-w-2xl rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Edit settlement</h2>
            <p className="text-sm text-gray-500">
              Update payment status, add notes, or attach supporting images.
            </p>
          </div>
          <button
            type="button"
            className="text-gray-500 hover:text-gray-700"
            onClick={() => !submitting && onClose?.()}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-5">
          <div className="grid gap-4 sm:grid-cols-2 text-sm">
            <div>
              <p className="text-gray-500">Settlement ID</p>
              <p className="font-medium text-gray-900">#{settlement.id}</p>
            </div>
            <div>
              <p className="text-gray-500">Shop</p>
              <p className="font-medium text-gray-900">{settlement.shop?.name || '—'}</p>
            </div>
            <div>
              <p className="text-gray-500">Payable amount</p>
              <p className="font-medium text-gray-900">{formatAmount(payableAmount)}</p>
            </div>
            <div>
              <p className="text-gray-500">Current balance</p>
              <p className="font-medium text-gray-900">{formatAmount(balanceAmount)}</p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="amountPaid" className="block text-sm font-medium text-gray-700">
                Amount paid
              </label>
              <input
                id="amountPaid"
                type="number"
                step="0.01"
                min="0"
                value={amountPaid}
                onChange={(event) => setAmountPaid(event.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label htmlFor="paymentStatus" className="block text-sm font-medium text-gray-700">
                Payment status
              </label>
              <select
                id="paymentStatus"
                value={status}
                onChange={(event) => setStatus(Number(event.target.value))}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                {PAYMENT_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="settlementNote" className="block text-sm font-medium text-gray-700">
              Notes
            </label>
            <textarea
              id="settlementNote"
              rows={3}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Add any remarks about this settlement"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Supporting image</p>
            <div className="flex items-center space-x-3">
              <label
                htmlFor="settlementSignature"
                className="inline-flex items-center space-x-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer"
              >
                <Upload size={16} />
                <span>{uploading ? 'Uploading...' : 'Upload image'}</span>
                <input
                  id="settlementSignature"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={uploading || submitting}
                  onChange={handleUpload}
                />
              </label>
              {signatureUrl && (
                <button
                  type="button"
                  onClick={() => setSignatureUrl(null)}
                  className="inline-flex items-center space-x-2 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                >
                  <Trash2 size={16} />
                  <span>Remove</span>
                </button>
              )}
            </div>
            {signatureUrl && (
              <a
                href={signatureUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-800"
              >
                View uploaded image
              </a>
            )}
          </div>

          <div className="flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={() => !submitting && onClose?.()}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:opacity-70"
            >
              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
